import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ResourceMapping } from '@ariksa/inventory-core/api';
import { Stack, Center } from '@chakra-ui/react';
import { each, identity, map, sortBy } from 'lodash';
import { useSelector } from 'react-redux';

import {
  getIcon,
  getResourceType as getResource,
  getResourceTypeOptions,
  getResourceTypeOptionsWithAlias,
  getResourceTypeOptionsWithAll,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { selectApp } from 'containers/App/selectors';

export const useResourceType = () => {
  const { resourceTypes } = useSelector(selectApp);
  const [
    cloudAgnosticNameClassMapping,
    setCloudAgnosticNameClassMapping,
  ] = useState<Record<string, any>>({});
  const [cloudNativeMapping, setCloudNativeMapping] = useState<
    Record<string, ResourceMapping>
  >({});
  const [providerResourcesMapping, setProviderResourcesMapping] = useState<
    Record<string, ResourceMapping[]>
  >({});

  useEffect(() => {
    let items: Record<string, ResourceMapping> = {};
    each(resourceTypes.data, o => {
      if (!!o?.cloud_native_class) {
        items[o.cloud_native_class!] = o;
      }
    });
    setCloudNativeMapping(items);
  }, [resourceTypes.data]);

  useEffect(() => {
    let items: Record<string, ResourceMapping[]> = {};
    each(resourceTypes.data, o => {
      if (!!o?.cloud_native_class && o?.provider) {
        if (!!items[o.provider]) items[o.provider].push(o);
        else items[o.provider] = [o];
      }
    });
    setProviderResourcesMapping(items);
  }, [resourceTypes.data]);

  /*const getResource = useCallback((name: string, type?: string) => {
    return resourceTypes.data?.find(item => {
      if (type === 'agnostic') return item.cloud_agnostic_class === name;
      return item.cloud_native_class === name;
    });
  }, []);*/

  const getResourceAlias = useCallback(
    (name: string) => {
      const data = getResource(name, resourceTypes.data);
      if (!!data) return data?.resource_alias ?? data?.cloud_native_name;
      return name;
    },
    [resourceTypes.data],
  );

  const getCloudAgnosticName = useCallback(
    (name: string) => {
      const data = getResource(name, resourceTypes.data, 'agnostic');
      if (!!data) return data?.cloud_agnostic_name;
      return name;
    },
    [resourceTypes.data],
  );

  const getCloudAgnosticClass = useCallback(
    (name: string) => {
      const data = getResource(name, resourceTypes.data, 'agnostic');
      if (!!data) return data?.cloud_agnostic_class;
      return name;
    },
    [resourceTypes.data],
  );

  const getCloudNativeName = useCallback(
    (name: string) => {
      const data = getResource(name, resourceTypes.data);
      if (!!data) return data?.cloud_native_name;
      return name;
    },
    [resourceTypes.data],
  );

  const getCloudNativeClass = useCallback(
    (name: string) => {
      const data = getResource(name, resourceTypes.data);
      if (!!data) return data?.cloud_native_class;
      return name;
    },
    [resourceTypes.data],
  );

  const getResourceType = useCallback(
    (name: string) => {
      return cloudNativeMapping[name] || ({} as ResourceMapping);
    },
    [cloudNativeMapping],
  );

  const resourceTypeOptions = useCallback(
    () => sortBy(getResourceTypeOptions(resourceTypes?.data), o => o?.label),
    [resourceTypes?.data],
  );

  const resourceTypeOptionsWithAlias = useCallback(
    () =>
      sortBy(
        getResourceTypeOptionsWithAlias(resourceTypes?.data),
        o => o?.label,
      ),
    [resourceTypes?.data],
  );

  const getResourceTypeOptionsOfProvider = useCallback(
    provider => [
      { label: 'Resource Type', value: undefined },
      ...sortBy(
        getResourceTypeOptionsWithAlias(
          providerResourcesMapping[provider] || [],
        ),
        o => o?.label,
      ),
    ],
    [providerResourcesMapping],
  );

  const resourceTypeAliasOptionsWithAll = useMemo(
    () => [
      { label: 'Resource type', value: undefined },
      ...sortBy(
        getResourceTypeOptionsWithAlias(resourceTypes?.data),
        o => o?.label,
      ),
    ],
    [resourceTypes?.data],
  );

  const resourceTypeAliasOptionsByCloud = useCallback(() => {
    let clouds: Record<string, any> = {};
    each(resourceTypes.data, o => {
      if (!!o.provider) {
        if (!!clouds[o.provider]) clouds[o.provider].push(o);
        else clouds[o.provider] = [o];
      }
    });
    return map(clouds, (resources, key) => ({
      label: key,
      options: map(resources, o => ({
        label: o?.resource_alias ?? o.cloud_native_name,
        value: o.cloud_native_class,
        data: o,
        icon: getIcon(o.cloud_native_class || ''),
      })),
    }));
  }, [resourceTypes?.data]);

  const agnosticResourceTypeOptionsWithAll = useMemo(
    () => [
      { label: 'All resource types', value: undefined },
      ...sortBy(
        map(resourceTypes.data, o => ({
          label: o?.cloud_agnostic_name,
          value: o.cloud_agnostic_class,
          //data: o,
          icon: getIcon(o.cloud_agnostic_class || ''),
        }))?.filter(identity),
        o => o?.label,
      ),
    ],
    [resourceTypes?.data],
  );

  const resourceTypeOptionsWithAll = useCallback(
    () => getResourceTypeOptionsWithAll(resourceTypes?.data),
    [resourceTypes?.data],
  );

  const getAlertResourceTypeOptions = useCallback(
    (types: string[]) => {
      return sortBy(
        map(types, o => ({
          label: getResourceAlias(o),
          value: o,
          icon: getIcon(o),
        })),
        s => s?.label,
      );
    },
    [getResourceAlias],
  );

  const getAlertResourceTypeOptionsWithAll = useCallback(
    (types: string[]) => {
      return [
        { label: 'Resource type', value: undefined },
        ...getAlertResourceTypeOptions(types),
      ];
    },
    [getAlertResourceTypeOptions],
  );

  return {
    getCloudAgnosticName,
    getCloudNativeName,
    getResourceType,
    getCloudAgnosticClass,
    getCloudNativeClass,
    getResourceAlias,
    getResourceTypeOptions: resourceTypeOptions,
    getResourceTypeOptionsWithAlias: resourceTypeOptionsWithAlias,
    resourceTypeOptionsWithAll,
    getAlertResourceTypeOptions,
    getAlertResourceTypeOptionsWithAll,
    resourceTypeAliasOptionsWithAll,
    agnosticResourceTypeOptionsWithAll,
    cloudNativeMapping,
    resourceTypeAliasOptionsByCloud,
    providerResourcesMapping,
    getResourceTypeOptionsOfProvider,
  };
};

export const ResourceTypeIconTooltip = ({ resourceType }) => {
  const { getCloudNativeName, getCloudAgnosticName } = useResourceType();
  return (
    <Stack spacing={0}>
      <Center color="orange">
        {getCloudNativeName(resourceType) ?? resourceType}
      </Center>
      <Center>{getCloudAgnosticName(resourceType) ?? resourceType}</Center>
    </Stack>
  );
};

export const StackedResourceType = ({ resourceType }) => {
  const { getCloudAgnosticName, getResourceAlias } = useResourceType();
  return (
    <WithResourceIcon
      resourceType={resourceType}
      iconTooltip={<ResourceTypeIconTooltip resourceType={resourceType} />}
      tooltip={<ResourceTypeIconTooltip resourceType={resourceType} />}
      iconSize="sm"
    >
      <StackedCell
        upper={getResourceAlias(resourceType) ?? resourceType}
        lower={getCloudAgnosticName(resourceType) ?? resourceType}
        showLowerTooltip={false}
        showUpperTooltip={false}
      />
    </WithResourceIcon>
  );
};
